/**
 * contexts/WarehouseContext.tsx
 * ──────────────────────────────
 * Provides the active warehouse scope across the entire session.
 *
 * Behaviour by role
 * ─────────────────
 * admin / super_admin
 *   • Fetches the full warehouse list from GET /api/manage/warehouses on mount.
 *   • activeWarehouseId is stored in sessionStorage so it survives page
 *     refreshes but resets when the tab is closed.
 *   • Defaults to null (all warehouses) if no selection has been made.
 *
 * pilot / manager / customer
 *   • activeWarehouseId is pinned to user.warehouse_id from the JWT — immutable.
 *   • No network call is made; the warehouse list contains the single entry
 *     returned from the JWT claim (or is empty if warehouse_id is null).
 *
 * The context is intentionally minimal — consumers call useWarehouse() and
 * read { warehouses, activeWarehouseId, activeWarehouse, setActiveWarehouseId,
 *         refreshWarehouses, isLoading }.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/api/client';
import type { WarehouseItem } from '@/types';

// ── Context shape ─────────────────────────────────────────────────────────────

interface WarehouseContextValue {
  /** Full list of warehouses the current user is allowed to see. */
  warehouses: WarehouseItem[];
  /** The currently scoped warehouse UUID, or null (admin "all warehouses"). */
  activeWarehouseId: string | null;
  /** Convenience: the WarehouseItem matching activeWarehouseId, or null. */
  activeWarehouse: WarehouseItem | null;
  /** Update the active warehouse (admin only — no-op for scoped roles). */
  setActiveWarehouseId: (id: string | null) => void;
  /** Re-fetch the warehouse list (admin only). Call after create/update/delete. */
  refreshWarehouses: () => Promise<void>;
  /** True while the warehouse list is being fetched. */
  isLoading: boolean;
}

const WarehouseContext = createContext<WarehouseContextValue | null>(null);

const SESSION_KEY = 'infinium_active_warehouse_id';

// ── Provider ──────────────────────────────────────────────────────────────────

export function WarehouseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [isLoading, setIsLoading]   = useState(false);

  // Initialise activeWarehouseId:
  // • Admin: restore from sessionStorage (may be null = "all")
  // • Other roles: pin to JWT claim (never changes)
  const [activeWarehouseId, _setActiveWarehouseId] = useState<string | null>(() => {
    if (!user) return null;
    if (isAdmin) {
      return sessionStorage.getItem(SESSION_KEY) ?? null;
    }
    return user.warehouse_id;
  });

  const fetchWarehouses = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    try {
      const data = await apiClient.get<WarehouseItem[]>('/api/manage/warehouses');
      setWarehouses(data);
    } catch {
      setWarehouses([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  // Fetch the warehouse list once the user is known.
  useEffect(() => {
    if (!user) return;
    if (isAdmin) {
      fetchWarehouses();
    } else {
      // Non-admin: we only know the single warehouse from the JWT.
      // We don't have the full name/code from the token, so leave the list
      // empty — consumers that need the label can fall back to the UUID.
      setWarehouses([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const setActiveWarehouseId = useCallback(
    (id: string | null) => {
      if (!isAdmin) return; // guard: non-admin warehouse is immutable
      _setActiveWarehouseId(id);
      if (id === null) {
        sessionStorage.removeItem(SESSION_KEY);
      } else {
        sessionStorage.setItem(SESSION_KEY, id);
      }
    },
    [isAdmin],
  );

  const activeWarehouse =
    warehouses.find(w => w.id === activeWarehouseId) ?? null;

  return (
    <WarehouseContext.Provider
      value={{
        warehouses,
        activeWarehouseId,
        activeWarehouse,
        setActiveWarehouseId,
        refreshWarehouses: fetchWarehouses,
        isLoading,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useWarehouse(): WarehouseContextValue {
  const ctx = useContext(WarehouseContext);
  if (!ctx) {
    throw new Error('useWarehouse must be used inside <WarehouseProvider>');
  }
  return ctx;
}
